import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { MainService } from 'src/app/app.service';
import { SmartReviewApiService } from './smart-review-api.service';
import { TfvcChangeset, TfvcFileInfo, TfvcHistoryResponse } from '../interfaces/job.interface';

interface RawTfvcFileItem {
  path?: string;
  filename?: string;
  name?: string;
  extension?: string;
  lastCheckInDate?: string;
  changeset?: string;
}

/** Integração TFVC do Smart Review: busca de arquivos e histórico de changesets. */
@Injectable()
export class TfvcService extends SmartReviewApiService {
  constructor(http: HttpClient, mainService: MainService) {
    super(http, mainService);
  }

  searchTfvcFiles(query: string): Observable<TfvcFileInfo[]> {
    const params = new HttpParams().set('query', query);

    return this.http
      .get<{ files: RawTfvcFileItem[] }>(`${this.apiURL}/tfvc/files`, {
        headers: this.jsonHeaders,
        params
      })
      .pipe(
        // Normaliza campos da API para o contrato interno (igual VS Code TfvcFileInfo)
        map(res => (res?.files ?? []).map((f): TfvcFileInfo => ({
          path: f.path || '',
          name: f.filename || f.name || (f.path ? (f.path.split('/').pop() ?? '') : '') || '',
          extension: f.extension || '',
          lastCheckInDate: f.lastCheckInDate || '',
          changeset: f.changeset ?? undefined
        }))),
        catchError(this.mainService.handleError)
      );
  }

  getTfvcFileHistory(path: string, continuationToken?: string, limit = 20): Observable<TfvcHistoryResponse> {
    let params = new HttpParams()
      .set('path', path)
      .set('limit', limit.toString());

    if (continuationToken?.trim()) {
      params = params.set('continuationToken', continuationToken.trim());
    }

    return this.http
      .get<TfvcHistoryResponse>(`${this.apiURL}/tfvc/files/history`, {
        headers: this.jsonHeaders,
        params
      })
      .pipe(
        map(res => ({
          changesets: this.extractHistoryChangesets(res),
          nextContinuationToken: this.extractNextContinuationToken(res)
        })),
        catchError(this.mainService.handleError)
      );
  }

  private extractHistoryChangesets(payload: any): TfvcChangeset[] {
    if (!payload || typeof payload !== 'object') { return []; }
    const candidates = [payload.changesets, payload.changeSets, payload.items];
    const found = candidates.find(Array.isArray);
    return Array.isArray(found) ? found as TfvcChangeset[] : [];
  }

  private extractNextContinuationToken(payload: any): string | undefined {
    if (!payload || typeof payload !== 'object') { return undefined; }

    const directValue =
      payload.nextContinuationToken ??
      payload.nextContinuationtoken ??
      payload.nextcontinuationToken ??
      payload.nextcontinuationtoken ??
      payload.continuationToken ??
      payload.continuationtoken ??
      payload.nextToken ??
      payload.nexttoken;

    const keyFromPayload = Object.keys(payload).find(
      key => key.toLowerCase() === 'nextcontinuationtoken'
    );
    const keyedValue = keyFromPayload ? payload[keyFromPayload] : undefined;
    const rawToken = directValue ?? keyedValue;
    const token = typeof rawToken === 'string' ? rawToken.trim() : String(rawToken ?? '').trim();
    return token || undefined;
  }
}
